import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { ThemeProvider } from './components/ThemeProvider';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import SettingsPage from './pages/SettingsPage';
import CreateMeetFlow from './pages/CreateMeetFlow';
import MeetSchedule from './pages/MeetSchedule';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AuthCallback from './components/AuthCallback';
import { registerDevice, syncOfflineMeets, getDeviceId, flushPendingMeetEnds } from './utils/api';
import { fetchCurrentUser, getCachedUser, logout as authLogout, applyOAuthRedirect, linkDevice } from './utils/auth';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

const MARKETING_URL = 'https://foresightpulse.com';

/**
 * Web is a public viewer for shared meet links. Anyone landing on the bare
 * domain (or any non-/meet path) is bounced to the marketing site so they can
 * download the app. There is intentionally NO web auth — meet creation lives
 * exclusively in the native app.
 */
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function WebRouter() {
  return (
    <Routes>
      {/* Public meet view — the only first-class web surface */}
      <Route path="/meet/:meetId" element={<MeetSchedule />} />
      {/* Required for App Store / Play Store compliance */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      {/* Everything else (including /, /login, /dashboard) redirects to the
          marketing site where users can download the app. */}
      <Route path="*" element={<ExternalRedirect to={MARKETING_URL} />} />
    </Routes>
  );
}

function NativeRouter({ isAuthenticated, setIsAuthenticated, handleLogout }) {
  const location = useLocation();

  // Synchronous check: if we returned from Supabase OAuth (URL fragment has
  // access_token), route through AuthCallback BEFORE any ProtectedRoute
  // logic runs. This covers the case where the OS opens the deep link as a
  // fresh in-WebView load instead of via the appUrlOpen event.
  if (location.hash?.includes('access_token=')) {
    return <AuthCallback onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  if (isAuthenticated === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background"
        data-testid="app-auth-loading"
      >
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/auth-callback"
        element={<AuthCallback onAuthenticated={() => setIsAuthenticated(true)} />}
      />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          isAuthenticated ? <Dashboard onLogout={handleLogout} /> : <Navigate to="/login" replace />
        }
      />
      <Route
        path="/settings"
        element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/create-meet"
        element={isAuthenticated ? <CreateMeetFlow /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/edit-meet/:meetId"
        element={isAuthenticated ? <CreateMeetFlow /> : <Navigate to="/login" replace />}
      />
      {/* Public meet view — accessible without login (shareable link) */}
      <Route path="/meet/:meetId" element={<MeetSchedule />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
    </Routes>
  );
}

export default function App() {
  const isNative = Capacitor.isNativePlatform();
  // null = checking, true = signed in, false = signed out (only meaningful on native)
  const [isAuthenticated, setIsAuthenticated] = useState(
    isNative ? (getCachedUser() ? true : null) : false
  );

  useEffect(() => {
    // Web is view-only — no device registration, no sync, no auth check.
    if (!isNative) return;

    const initApp = async () => {
      try {
        const platform = Capacitor.getPlatform();
        await registerDevice(platform);

        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the tokens and set state itself.
        if (window.location.hash?.includes('access_token=')) {
          return;
        }

        const user = await fetchCurrentUser();
        setIsAuthenticated(!!user);

        const summary = await syncOfflineMeets();
        if (summary && summary.synced > 0) {
          toast.success(
            `Synced ${summary.synced} offline ${summary.synced === 1 ? 'meet' : 'meets'} to the cloud.`
          );
        }
        if (summary && summary.paywalled > 0) {
          toast.info(
            `${summary.paywalled} offline ${summary.paywalled === 1 ? 'meet is' : 'meets are'} waiting — upgrade to Premium to save ${summary.paywalled === 1 ? 'it' : 'them'}.`
          );
        }

        // Drain any queued meet-end requests that auto-end couldn't deliver
        // (device was offline / app force-quit before the POST completed).
        flushPendingMeetEnds();
      } catch (error) {
        console.error('App initialization error:', error);
        setIsAuthenticated(false);
      }
    };

    initApp();

    // Native deep-link listener: captures the OAuth callback URL when the OS
    // hands it to us via Android App Links / iOS Universal Links.
    let removeUrlListener = null;
    (async () => {
      try {
        const handle = await CapacitorApp.addListener('appUrlOpen', async (event) => {
          try {
            const url = event?.url || '';
            // Only handle our auth-callback deep link
            if (!url.includes('/auth-callback') && !url.includes('access_token=')) return;
            // Close the in-app system browser tab if one is open
            try { await Browser.close(); } catch (_) { /* ignore */ }
            const user = await applyOAuthRedirect(url);
            const deviceId = getDeviceId();
            if (deviceId) await linkDevice(deviceId);
            setIsAuthenticated(true);
            // Pull fresh meets now that the user is identified
            try { await syncOfflineMeets(); } catch (_) { /* ignore */ }
            const meta = user?.user_metadata || {};
            const displayName = meta.full_name || meta.name || user?.email || '';
            toast.success(`Welcome${displayName ? `, ${String(displayName).split(' ')[0]}` : ''}!`);
          } catch (err) {
            console.error('Deep-link auth failed:', err);
            toast.error('Sign-in did not complete. Please try again.');
          }
        });
        removeUrlListener = handle?.remove ? () => handle.remove() : null;
      } catch (e) {
        console.error('Could not register appUrlOpen listener:', e);
      }
    })();

    const onBackOnline = async () => {
      try {
        const summary = await syncOfflineMeets();
        if (summary && summary.synced > 0) {
          toast.success(
            `Back online — synced ${summary.synced} pending ${summary.synced === 1 ? 'meet' : 'meets'}.`
          );
        }
        // Also retry any queued meet-end POSTs that auto-end couldn't push
        // before the device went offline / the app was force-quit.
        flushPendingMeetEnds();
      } catch (err) {
        console.error('Online resync error:', err);
      }
    };
    window.addEventListener('online', onBackOnline);

    // Periodic safety-net for queued meet-end POSTs. The `online` event only
    // fires on offline→online transitions, so if the auto-end POST failed
    // while the device stayed online the whole time (transient backend
    // hiccup, brief 5xx, etc.) the queue would sit untouched until the next
    // app start. A cheap 30s timer drains it whenever the device is online.
    // flushPendingMeetEnds() is a no-op when the queue is empty.
    const pendingEndsInterval = setInterval(() => {
      flushPendingMeetEnds().catch(() => { /* logged inside */ });
    }, 30000);

    return () => {
      window.removeEventListener('online', onBackOnline);
      clearInterval(pendingEndsInterval);
      if (removeUrlListener) {
        try { removeUrlListener(); } catch (_) { /* ignore */ }
      }
    };
  }, [isNative]);

  const handleLogout = async () => {
    await authLogout();
    setIsAuthenticated(false);
  };

  return (
    <ThemeProvider defaultTheme="light" storageKey="foresight-theme">
      <Router>
        <div className="min-h-screen bg-background">
          {isNative ? (
            <NativeRouter
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={setIsAuthenticated}
              handleLogout={handleLogout}
            />
          ) : (
            <WebRouter />
          )}
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              description: 'Swipe to dismiss',
            }}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
}
